// WithdrawFlowPage — Multi-step withdrawal flow
// Steps: 1) Method → 2) Amount → 3) Bank selection → 4) Review → 5) Submit

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PartnerLogo } from '../../components/Brand';

interface BankAccount {
  id: string;
  type: 'checking' | 'savings';
  bankName: string;
  last4: string;
  verified: boolean;
  isDefault?: boolean;
}

const BANKS: BankAccount[] = [
  { id: 'b1', type: 'checking', bankName: 'Chase Bank',        last4: '4521', verified: true,  isDefault: true },
  { id: 'b2', type: 'savings',  bankName: 'Bank of America',   last4: '8932', verified: true },
  { id: 'b3', type: 'checking', bankName: 'Wells Fargo',       last4: '1107', verified: false },
];

const METHODS = [
  { key: 'ach',     label: 'ACH transfer',    sub: '1-2 business days', fee: 'Free',     min: 50,   max: 50000,  icon: '🏦', accent: '#34C759' },
  { key: 'wire',    label: 'Wire transfer',   sub: 'Same day',          fee: '$25',      min: 500,  max: 250000, icon: '🌐', accent: '#FF9500' },
  { key: 'crypto',  label: 'Crypto (USDC)',   sub: '~5 min',            fee: 'Network',  min: 100,  max: 100000, icon: '🪙', accent: '#5856D6' },
  { key: 'paypal',  label: 'PayPal',          sub: 'Instant',           fee: 'Free',     min: 25,   max: 10000,  icon: '🅿️', accent: '#007AFF' },
] as const;
type MethodKey = typeof METHODS[number]['key'];

export function WithdrawFlowPage() {
  const nav = useNavigate();
  const [step, setStep] = useState<'method' | 'amount' | 'bank' | 'review' | 'submitting' | 'done'>('method');
  const [method, setMethod] = useState<MethodKey | null>('ach');
  const [amount, setAmount] = useState('');
  const [bank, setBank] = useState(BANKS[0].id);
  const m = METHODS.find((x) => x.key === method);

  function next() {
    if (step === 'method' && method) setStep('amount');
    else if (step === 'amount' && Number(amount) > 0) {
      if (method === 'ach' || method === 'wire') setStep('bank');
      else setStep('review');
    }
    else if (step === 'bank') setStep('review');
    else if (step === 'review') {
      setStep('submitting');
      setTimeout(() => setStep('done'), 2000);
    }
  }
  function back() {
    if (step === 'amount') setStep('method');
    else if (step === 'bank') setStep('amount');
    else if (step === 'review') (method === 'ach' || method === 'wire') ? setStep('bank') : setStep('amount');
    else nav(-1);
  }

  return (
    <div className="min-h-screen bg-tf-surface-1 flex flex-col">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border ios26-navbar px-2 backdrop-blur-xl">
        <button onClick={back} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue haptic">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">Withdraw</div>
        <button onClick={() => nav(-1)} data-testid="cancel" className="px-3 text-[15px] text-tf-blue">Cancel</button>
      </div>

      <div className="flex-1 overflow-y-auto pb-32">
        {step === 'method'  && <MethodStep  method={method} setMethod={setMethod} />}
        {step === 'amount'  && m && <AmountStep  method={m} amount={amount} setAmount={setAmount} />}
        {step === 'bank'    && <BankStep    bank={bank} setBank={setBank} />}
        {step === 'review'  && m && <ReviewStep method={m} amount={amount} bankId={bank} />}
        {step === 'submitting' && <SubmittingStep />}
        {step === 'done'    && m && <DoneStep method={m} amount={amount} onContinue={() => nav('/portal/wallet')} />}
      </div>

      {step !== 'done' && step !== 'submitting' && (
        <div className="fixed inset-x-0 bottom-0 z-10 bg-white/80 backdrop-blur-xl border-t border-tf-border px-4 py-3" style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}>
          <button
            onClick={next}
            disabled={(step === 'method' && !method) || (step === 'amount' && (!amount || Number(amount) < (m?.min || 0)))}
            data-testid="withdraw-next"
            className="flex h-12 w-full items-center justify-center btn-spring disabled:opacity-40"
          >
            {step === 'method' ? 'Continue' : step === 'amount' ? (m?.key === 'ach' || m?.key === 'wire' ? 'Choose bank' : 'Review') : step === 'bank' ? 'Review' : 'Submit withdrawal'}
          </button>
        </div>
      )}
    </div>
  );
}

function MethodStep({ method, setMethod }: { method: MethodKey | null; setMethod: (m: MethodKey) => void }) {
  return (
    <div className="px-4 pt-4">
      <div className="rounded-[20px] bg-white p-4 mb-4 flex items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-[14px] bg-[#34C759]/15 text-[24px]">💰</div>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-tf-text-3">Available to withdraw</div>
          <div className="text-[22px] font-bold tabular-nums text-tf-text-1">$4,250.00</div>
        </div>
      </div>
      <h2 className="text-[22px] font-bold tracking-tight text-tf-text-1">Choose method</h2>
      <p className="mt-1 mb-4 text-[14px] text-tf-text-3">Pick how you'd like to receive your funds.</p>
      <div className="ios26-list bg-white">
        {METHODS.map((m, i) => (
          <button
            key={m.key}
            onClick={() => setMethod(m.key)}
            data-testid={`wmethod-${m.key}`}
            className={['flex w-full items-center gap-3 px-4 py-3 active:bg-tf-surface-1 transition', i < METHODS.length - 1 ? 'border-b border-tf-border' : ''].join(' ')}
          >
            <div className="flex h-9 w-9 items-center justify-center rounded-[10px] text-[20px]" style={{ backgroundColor: m.accent + '18' }}>{m.icon}</div>
            <div className="flex-1 min-w-0 text-left">
              <div className="text-[14px] font-semibold text-tf-text-1">{m.label}</div>
              <div className="text-[11px] text-tf-text-3">{m.sub} · Fee {m.fee}</div>
            </div>
            {method === m.key && (
              <svg viewBox="0 0 24 24" className="h-5 w-5 text-tf-blue" fill="currentColor"><path d="M9 16.17 5.53 12.7a1 1 0 0 0-1.41 1.41l4.18 4.18a1 1 0 0 0 1.41 0L20.29 8.71a1 1 0 0 0-1.41-1.41z" /></svg>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

function AmountStep({ method, amount, setAmount }: { method: typeof METHODS[number]; amount: string; setAmount: (v: string) => void }) {
  const num = Number(amount) || 0;
  const fee = method.fee === 'Free' ? 0 : method.fee === 'Network' ? 2 : method.fee === '$25' ? 25 : 0;

  return (
    <div className="px-4 pt-4">
      <h2 className="text-[22px] font-bold tracking-tight text-tf-text-1">Amount</h2>
      <p className="mt-1 mb-4 text-[14px] text-tf-text-3">Available: $4,250.00 · Min ${method.min.toLocaleString()}</p>

      <div className="rounded-[20px] bg-white p-6 text-center">
        <div className="text-[12px] uppercase tracking-wider text-tf-text-3">Withdrawing</div>
        <div className="mt-1 flex items-center justify-center">
          <span className="text-[32px] font-bold text-tf-text-1">$</span>
          <input
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0"
            data-testid="withdraw-amount"
            className="ml-1 w-48 bg-transparent text-[44px] font-bold text-tf-text-1 tabular-nums focus:outline-none text-center"
          />
        </div>
        <div className="mt-2 flex items-center justify-center gap-2">
          <button onClick={() => setAmount('4250')} className="rounded-full bg-tf-surface-1 px-3 py-1 text-[11px] font-semibold text-tf-blue active:opacity-40">Max</button>
          <button onClick={() => setAmount('1000')} className="rounded-full bg-tf-surface-1 px-3 py-1 text-[11px] font-semibold text-tf-blue active:opacity-40">$1,000</button>
          <button onClick={() => setAmount('500')} className="rounded-full bg-tf-surface-1 px-3 py-1 text-[11px] font-semibold text-tf-blue active:opacity-40">$500</button>
        </div>
      </div>

      <div className="mt-4 ios26-list bg-white">
        <div className="flex items-center justify-between px-4 py-3 border-b border-tf-border">
          <span className="text-[13px] text-tf-text-3">Withdrawal amount</span>
          <span className="text-[13px] font-semibold tabular-nums text-tf-text-1">${num.toFixed(2)}</span>
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-b border-tf-border">
          <span className="text-[13px] text-tf-text-3">Fee</span>
          <span className="text-[13px] font-semibold tabular-nums text-tf-text-1">${fee.toFixed(2)}</span>
        </div>
        <div className="flex items-center justify-between px-4 py-3">
          <span className="text-[13px] text-tf-text-3">You'll receive</span>
          <span className="text-[16px] font-bold tabular-nums text-tf-profit">${(num - fee).toFixed(2)}</span>
        </div>
      </div>
    </div>
  );
}

function BankStep({ bank, setBank }: { bank: string; setBank: (b: string) => void }) {
  return (
    <div className="px-4 pt-4">
      <h2 className="text-[22px] font-bold tracking-tight text-tf-text-1">Bank account</h2>
      <p className="mt-1 mb-4 text-[14px] text-tf-text-3">Select where to send your funds.</p>
      <div className="ios26-list bg-white">
        {BANKS.map((b, i) => (
          <button
            key={b.id}
            onClick={() => b.verified && setBank(b.id)}
            data-testid={`bank-${b.id}`}
            className={['flex w-full items-center gap-3 px-4 py-3 transition',
              i < BANKS.length - 1 ? 'border-b border-tf-border' : '',
              b.verified ? 'active:bg-tf-surface-1' : 'opacity-50'].join(' ')}
          >
            <div className="flex h-9 w-9 items-center justify-center rounded-[10px] bg-tf-blue/10 text-[18px]">🏦</div>
            <div className="flex-1 min-w-0 text-left">
              <div className="flex items-center gap-1.5">
                <span className="text-[14px] font-semibold text-tf-text-1">{b.bankName}</span>
                {b.isDefault && <span className="rounded bg-tf-text-1 px-1 py-0.5 text-[8px] font-bold uppercase text-white">Default</span>}
              </div>
              <div className="text-[11px] text-tf-text-3">{b.type === 'checking' ? 'Checking' : 'Savings'} · ••••{b.last4}</div>
            </div>
            <div>
              {b.verified ? (
                bank === b.id ? (
                  <svg viewBox="0 0 24 24" className="h-5 w-5 text-tf-blue" fill="currentColor"><path d="M9 16.17 5.53 12.7a1 1 0 0 0-1.41 1.41l4.18 4.18a1 1 0 0 0 1.41 0L20.29 8.71a1 1 0 0 0-1.41-1.41z" /></svg>
                ) : (
                  <span className="rounded-full bg-[#34C759]/15 px-1.5 py-0.5 text-[9px] font-bold uppercase text-tf-profit">✓</span>
                )
              ) : (
                <span className="rounded-full bg-[#FF9500]/15 px-1.5 py-0.5 text-[9px] font-bold uppercase text-tf-warning">Verify</span>
              )}
            </div>
          </button>
        ))}
        <button data-testid="add-bank" className="flex w-full items-center gap-3 px-4 py-3 text-tf-blue active:bg-tf-surface-1 transition">
          <div className="flex h-9 w-9 items-center justify-center rounded-[10px] bg-tf-blue/10 text-[18px]">+</div>
          <span className="text-[14px] font-semibold">Add new bank account</span>
        </button>
      </div>

      <div className="mt-4 rounded-[14px] border border-[#007AFF]/30 bg-tf-blue/[0.04] p-3">
        <div className="flex gap-2.5">
          <span className="text-[18px]">🛡️</span>
          <div>
            <div className="text-[13px] font-semibold text-tf-blue">Bank-level encryption</div>
            <p className="mt-0.5 text-[12px] text-tf-text-1">Account numbers are tokenized with Plaid. Powered by <PartnerLogo name="stripe" height={14} className="inline-block align-middle" /></p>
          </div>
        </div>
      </div>
    </div>
  );
}

function ReviewStep({ method, amount, bankId }: { method: typeof METHODS[number]; amount: string; bankId: string }) {
  const num = Number(amount);
  const fee = method.fee === 'Free' ? 0 : method.fee === 'Network' ? 2 : method.fee === '$25' ? 25 : 0;
  const b = BANKS.find((x) => x.id === bankId);
  return (
    <div className="px-4 pt-4">
      <h2 className="text-[22px] font-bold tracking-tight text-tf-text-1">Review</h2>
      <p className="mt-1 mb-4 text-[14px] text-tf-text-3">Double-check everything before submitting.</p>

      <div className="rounded-[20px] bg-white p-5 text-center">
        <div className="text-[12px] uppercase tracking-wider text-tf-text-3">You'll receive</div>
        <div className="mt-1 text-[44px] font-bold text-tf-profit tabular-nums">${(num - fee).toFixed(2)}</div>
        <div className="mt-1 text-[13px] text-tf-text-3">to {b?.bankName} ••••{b?.last4}</div>
      </div>

      <h3 className="mt-5 px-1 text-[13px] font-semibold uppercase tracking-wide text-tf-text-3">Details</h3>
      <div className="ios26-list bg-white">
        <Row label="Method"        value={method.label} />
        <Row label="Amount"        value={`$${num.toFixed(2)}`} />
        <Row label="Fee"           value={`$${fee.toFixed(2)}`} />
        <Row label="Net"           value={`$${(num - fee).toFixed(2)}`} bold />
        {b && <Row label="Bank"      value={`${b.bankName} ••••${b.last4}`} />}
        <Row label="Arrives"       value={method.sub} />
      </div>

      <div className="mt-4 rounded-[14px] border border-[#FF9500]/30 bg-[#FF9500]/[0.04] p-3">
        <div className="flex gap-2.5">
          <span className="text-[18px]">⏱️</span>
          <div>
            <div className="text-[13px] font-semibold text-tf-warning">Processing time</div>
            <p className="mt-0.5 text-[12px] text-tf-text-1">{method.label} takes {method.sub}. You'll get a notification when funds arrive.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function SubmittingStep() {
  return (
    <div className="flex flex-col items-center justify-center pt-24 px-4 text-center">
      <div className="relative h-20 w-20">
        <svg viewBox="0 0 100 100" className="h-20 w-20 animate-spin" style={{ animationDuration: '1.2s' }}>
          <circle cx="50" cy="50" r="42" stroke="#F2F2F7" strokeWidth="6" fill="none" />
          <circle cx="50" cy="50" r="42" stroke="#34C759" strokeWidth="6" fill="none" strokeLinecap="round" strokeDasharray="80 184" />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center text-[28px]">💸</div>
      </div>
      <h2 className="mt-6 text-[20px] font-bold tracking-tight text-tf-text-1">Submitting withdrawal</h2>
      <p className="mt-1 text-[13px] text-tf-text-3">Don't close this page</p>
    </div>
  );
}

function DoneStep({ method, amount, onContinue }: { method: typeof METHODS[number]; amount: string; onContinue: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center pt-16 px-4 text-center">
      <div className="flex h-20 w-20 items-center justify-center rounded-full bg-[#34C759] text-white">
        <svg viewBox="0 0 24 24" className="h-12 w-12" fill="none" stroke="currentColor" strokeWidth="3"><path d="M5 12l5 5L20 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
      </div>
      <h2 className="mt-6 text-[24px] font-bold tracking-tight text-tf-text-1">Withdrawal submitted</h2>
      <p className="mt-1 text-[14px] text-tf-text-3">${Number(amount).toFixed(2)} via {method.label}</p>
      <p className="mt-0.5 text-[12px] text-tf-text-3">Arrives in {method.sub}</p>
      <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#34C759]/15 px-3 py-1.5">
        <span className="h-1.5 w-1.5 rounded-full bg-[#34C759]" />
        <span className="text-[12px] font-semibold text-tf-profit">TX-7C29A84F</span>
      </div>
      <button onClick={onContinue} data-testid="withdraw-done" className="mt-8 h-12 w-full max-w-xs btn-spring">Back to wallet</button>
    </div>
  );
}

function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div className="flex items-center justify-between border-b border-tf-border last:border-b-0 px-4 py-2.5">
      <span className="text-[12px] text-tf-text-3">{label}</span>
      <span className={['tabular-nums', bold ? 'text-[15px] font-bold text-tf-text-1' : 'text-[13px] font-medium text-tf-text-1'].join(' ')}>{value}</span>
    </div>
  );
}
